"use client";

import { useIsShopsite } from "@context/DomainContext";
import { joinClassNames } from "@utils/helpers";
import { Button as RadixButton, Slot } from "@radix-ui/themes";
import Link, { LinkProps } from "next/link";
import { forwardRef, PropsWithChildren, ComponentProps } from "react";

type ButtonProps = ComponentProps<typeof RadixButton>;

interface Props {
  sharedClasses?: string;
  shopsiteClasses?: string;
  whitelabelClasses?: string;
  shopsiteVariant?:
    | "solid"
    | "soft"
    | "ghost"
    | "white"
    | "bordered-red"
    | "outline"
    | "iticket"
    | "iticket-soft";
  whitelabelVariant?: ButtonProps["variant"];
  href?: LinkProps["href"];
  target?: string;
  rel?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
}

export default forwardRef(function Button(
  {
    className = "",
    sharedClasses = "",
    shopsiteClasses = "",
    whitelabelClasses = "",
    shopsiteVariant = "solid",
    whitelabelVariant,
    asChild,
    children,
    href,
    target,
    rel,
    disabled,
    onClick,
    highContrast,
    ...restProps
  }: PropsWithChildren<Props> & Omit<ButtonProps, "onClick">,
  ref: React.Ref<HTMLButtonElement>
) {
  const isShopsite = useIsShopsite();

  const shopsiteVariantClasses =
    shopsiteVariant === "solid"
      ? "font-medium bg-indigo-500 hover:bg-indigo-400 disabled:bg-indigo-200 disabled:text-white text-white"
      : shopsiteVariant === "soft"
      ? "font-medium bg-indigo-100 hover:bg-indigo-500 disabled:bg-indigo-200 disabled:text-indigo-800/70 text-indigo-800 hover:text-white"
      : shopsiteVariant === "ghost"
      ? "font-medium text-black"
      : shopsiteVariant === "white"
      ? "bg-white font-normal border border-gray-300 shadow-sm hover:bg-gray-100"
      : shopsiteVariant === "bordered-red"
      ? "border-2 border-red-500 hover:bg-red-50 text-red-500 font-medium"
      : shopsiteVariant === "outline"
      ? "font-medium border border-indigo-500 hover:bg-indigo-100 disabled:bg-indigo-200 text-indigo-500"
      : shopsiteVariant === "iticket"
      ? "font-medium bg-iticket hover:bg-iticket/80 text-white disabled:bg-iticket/70"
      : shopsiteVariant === "iticket-soft"
      ? "font-medium bg-iticket-light hover:bg-iticket text-iticket-dark hover:text-white"
      : "";

  const shopsiteClassName = joinClassNames(
    "flex items-center justify-center rounded-md disabled:cursor-not-allowed transition-all duration-200 ease-in-out",
    shopsiteVariant === "bordered-red" ? "p-1.5" : "p-2",
    shopsiteVariantClasses,
    className,
    sharedClasses,
    shopsiteClasses
  );

  if (isShopsite) {
    return href ? (
      <Link
        href={href}
        target={target}
        rel={rel}
        aria-disabled={disabled}
        className={shopsiteClassName}
        onClick={onClick}
        aria-current={restProps["aria-current"] ?? undefined}
      >
        {children}
      </Link>
    ) : asChild ? (
      <Slot ref={ref} className={shopsiteClassName}>
        {children}
      </Slot>
    ) : (
      <button
        ref={ref}
        disabled={disabled}
        className={shopsiteClassName}
        onClick={onClick}
        {...restProps}
      >
        {children}
      </button>
    );
  }

  return (
    <RadixButton
      className={joinClassNames(
        "transition-colors ease-in-out cursor-pointer disabled:cursor-not-allowed",
        className,
        sharedClasses,
        whitelabelClasses
      )}
      asChild={!!href || asChild}
      onClick={onClick}
      variant={whitelabelVariant}
      highContrast={highContrast}
      disabled={disabled}
      {...restProps}
      ref={ref}
    >
      {href ? (
        <Link
          href={href}
          target={target}
          rel={rel}
          aria-current={restProps["aria-current"] ?? undefined}
        >
          {children}
        </Link>
      ) : (
        children
      )}
    </RadixButton>
  );
});
